import i18n from 'i18next'
import get from 'lodash/get'

import { Segment } from 'api/Cerebro/orgScoped/segments/types'
import {
    DimensionRenderer,
    SegmentProductInfo,
    WeightRenderer,
} from 'components/FieldRenderers/Renderers/segmentProduct'
import { GROUPING_COLORS } from 'configuration/charts'
import {
    asin_count__sum,
    price__last_value,
    revenue_1p__sum,
    revenue_3p__sum,
    revenue__sum,
    review_count__avg,
    sales_price_1p__avg,
    sales_price_3p__avg,
    sales_price__avg,
    units_1p__sum,
    units_3p__sum,
    units__sum,
    variant_count__total,
    variant_revenue_1p__sum,
    variant_revenue_3p__sum,
    variant_revenue__sum,
    variant_sales_price_1p__avg,
    variant_sales_price_3p__avg,
    variant_sales_price__avg,
    variant_units_1p__sum,
    variant_units_3p__sum,
    variant_units__sum,
} from 'configuration/fieldCreators/metrics/asinSalesEstimateFacts'
import {
    dimensions,
    weight,
} from 'configuration/fieldCreators/metrics/segmentProduct'
import {
    productId,
    productInfoPlaceholder,
    segmentId,
    sellerName,
} from 'configuration/fieldCreators/segments'
import {
    ADVANCED_PRODUCT_TITLE,
    SEGMENT_BRANDS,
    COUNTRIES,
    DATES,
    METRIC_FILTERS,
    SEGMENTS,
    SELLER_NAME,
    SOV_ASINS,
    SOV_BRANDS,
} from 'const/filters'
import { UNKNOWN_VALUE } from 'const/formatting'
import { DATE_GROUP_BY_FORMATTERS } from 'helpers/charts'
import { formatSegmentProductName } from 'helpers/formatters'
import { Field, FieldMapping } from 'types'
import { ChartFormatters } from 'types/dataSources'

import { makeMapping } from './helpers'

export const SPECIAL_SEGMENT_FACTS_GROUP_BY_COLUMNS: Field<Segment>[] = [
    productId({ dataIndex: ['product', 'id'] }),
    segmentId({ isVisible: true, dataIndex: ['segment', 'id'] }),
    sellerName(),
]

export const SPECIAL_SEGMENT_FACTS_GROUP_BY_COLUMN_PLACEHOLDERS = [
    productInfoPlaceholder({ dataIndex: ['product', 'id'] }),
]

export const SEGMENT_FACTS_GROUP_BY_CONFIG: ChartFormatters = {
    ...DATE_GROUP_BY_FORMATTERS,
    vendor_seller: {
        color: (record) =>
            get(GROUPING_COLORS, ['vendor_seller', record.vendor_seller]),
    },
    product_id: {
        displayName: (record) => formatSegmentProductName(record, 'product'),
        color: (record, { colorMapper }) => {
            const prop = get(record, 'product_id')
            return colorMapper(prop)
        },
    },
    product_info: {
        displayName: (record: SegmentProductInfo) =>
            formatSegmentProductName(record, 'product', {
                includeAsin: true,
                maxTitleLength: 30,
            }),
        color: (record, { colorMapper }) => {
            const prop = get(record, 'product_id')
            return colorMapper(prop)
        },
    },
    segment_id: {
        displayName: (record) => get(record, ['segment', 'name'], 'No Segment'),
    },
    seller_name: {
        displayName: (record) => {
            const name = get(record, 'seller_name')
            return name ?? UNKNOWN_VALUE
        },
        tooltipExtra: (record) => {
            const name = get(record, 'seller_name')
            return name ? '' : i18n.t('segments:Tooltip.Table.unknownSeller')
        },
    },
}

export const SPECIAL_SEGMENT_FACTS_METRIC_COLUMNS = [
    weight({
        renderOptions: {
            render: (cellRenderProps) => WeightRenderer<any>(cellRenderProps),
        },
        excludeLastPeriod: true,
    }),
    dimensions({
        renderOptions: {
            render: (cellRenderProps) =>
                DimensionRenderer<any>(cellRenderProps),
        },
        excludeLastPeriod: true,
    }),
]

export type SalesEstimatesMetricView = 'variants' | 'parents' | 'brands'

const PLE_SEGMENT_FACTS_METRICS_COLUMNS: Field<Segment>[] = [
    revenue__sum({ isVisible: true }),
    revenue_1p__sum({ isVisible: true }),
    revenue_3p__sum({ isVisible: true }),
    units__sum({ isVisible: true }),
    units_1p__sum({ isVisible: true }),
    units_3p__sum({ isVisible: true }),
    price__last_value({ isVisible: true }),
    sales_price__avg({ isVisible: true }),
    sales_price_1p__avg({ isVisible: true }),
    sales_price_3p__avg({ isVisible: true }),
    review_count__avg({ isVisible: true }),
    variant_count__total({ excludeLastPeriod: true }),
] as Field<Segment>[]

export const PLE_SEGMENT_FACTS_PARENTS_VIEW_METRICS: FieldMapping = makeMapping(
    PLE_SEGMENT_FACTS_METRICS_COLUMNS
)

export const VLE_SEGMENT_FACTS_VARIANTS_VIEW_METRICS: FieldMapping =
    makeMapping([
        variant_revenue__sum({ isVisible: true }),
        variant_revenue_1p__sum({ isVisible: true }),
        variant_revenue_3p__sum({ isVisible: true }),
        variant_units__sum({ isVisible: true }),
        variant_units_1p__sum({ isVisible: true }),
        variant_units_3p__sum({ isVisible: true }),
        price__last_value({ isVisible: true }),
        variant_sales_price__avg({ isVisible: true }),
        variant_sales_price_1p__avg({ isVisible: true }),
        variant_sales_price_3p__avg({ isVisible: true }),
        review_count__avg({ isVisible: true }),
    ] as Field<Segment>[])

export const VLE_SEGMENT_FACTS_PARENTS_VIEW_METRICS: FieldMapping = makeMapping(
    [
        variant_revenue__sum({ isVisible: true }),
        variant_revenue_1p__sum({ isVisible: true }),
        variant_revenue_3p__sum({ isVisible: true }),
        variant_units__sum({ isVisible: true }),
        variant_units_1p__sum({ isVisible: true }),
        variant_units_3p__sum({ isVisible: true }),
        variant_sales_price__avg({ isVisible: true }),
        variant_sales_price_1p__avg({ isVisible: true }),
        variant_sales_price_3p__avg({ isVisible: true }),
        review_count__avg({ isVisible: true }),
        variant_count__total({ excludeLastPeriod: true }),
    ] as Field<Segment>[]
)

export const VLE_SEGMENT_FACTS_BRANDS_VIEW_METRICS: FieldMapping = makeMapping([
    variant_revenue__sum({ isVisible: true }),
    variant_revenue_1p__sum({ isVisible: true }),
    variant_revenue_3p__sum({ isVisible: true }),
    variant_units__sum({ isVisible: true }),
    variant_units_1p__sum({ isVisible: true }),
    variant_units_3p__sum({ isVisible: true }),
    variant_sales_price__avg({ isVisible: true }),
    variant_sales_price_1p__avg({ isVisible: true }),
    variant_sales_price_3p__avg({ isVisible: true }),
    review_count__avg({ isVisible: true }),
    asin_count__sum({ isVisible: true }),
] as Field<Segment>[])

export const ALL_SEGMENT_FACTS_VIEW_METRICS: FieldMapping = {
    ...PLE_SEGMENT_FACTS_PARENTS_VIEW_METRICS,
    ...VLE_SEGMENT_FACTS_VARIANTS_VIEW_METRICS,
    ...VLE_SEGMENT_FACTS_PARENTS_VIEW_METRICS,
    ...VLE_SEGMENT_FACTS_BRANDS_VIEW_METRICS,
}

export const SEGMENT_FACTS_FILTER = [
    DATES,
    COUNTRIES,
    SEGMENTS,
    SELLER_NAME,
    ADVANCED_PRODUCT_TITLE,
    SEGMENT_BRANDS,
    SOV_BRANDS,
    SOV_ASINS,
    METRIC_FILTERS,
]
